<template>
    <div class="admin-pages">
        <!-- 工具栏 -->
        <div class="a-card">
            <div class="a-toolbar">
                <input
                    class="a-input"
                    style="width: 220px"
                    v-model="query.keyword"
                    placeholder="搜索标题 / 摘要 / 路由标识"
                    @keyup.enter="search"
                />
                <select class="a-select" v-model="query.status" @change="search">
                    <option value="all">全部状态</option>
                    <option value="1">已发布</option>
                    <option value="0">草稿</option>
                    <option value="2">已下线</option>
                </select>
                <button class="a-btn primary" @click="search">查询</button>
                <button class="a-btn" @click="reset">重置</button>

                <span class="grow"></span>

                <button class="a-btn" :disabled="loading" @click="load">刷新</button>
                <button class="a-btn" :disabled="seeding" @click="doSeed">
                    {{ seeding ? '导入中…' : '重新导入内置页' }}
                </button>
                <button class="a-btn primary" @click="$router.push('/admin/edit')">＋ 新建页面</button>
            </div>

            <!-- 列表 -->
            <div v-if="loading" class="a-empty">正在加载…</div>
            <div v-else-if="!items.length" class="a-empty">
                还没有内容，点右上角「新建页面」开始，或「重新导入内置页」把内置的 5 封信导进来。
            </div>
            <table v-else class="a-table">
                <thead>
                    <tr>
                        <th class="col-id">ID</th>
                        <th>标题</th>
                        <th style="width: 92px">主题</th>
                        <th style="width: 120px">标签</th>
                        <th style="width: 96px">内容</th>
                        <th style="width: 88px">状态</th>
                        <th style="width: 78px">排序</th>
                        <th style="width: 76px">浏览</th>
                        <th style="width: 148px">更新时间</th>
                        <th style="width: 250px">操作</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="it in items" :key="it.id">
                        <td class="col-id">{{ it.id }}</td>
                        <td>
                            <div class="a-title-cell">
                                <div class="t-main">{{ it.title || '(无标题)' }}</div>
                                <div class="t-code"><code>{{ it.code }}</code></div>
                            </div>
                        </td>
                        <td>
                            <span class="theme-chip" :class="it.theme">{{ themeLabel(it.theme) }}</span>
                        </td>
                        <td>{{ it.tag || '—' }}</td>
                        <td class="muted">{{ it.paragraphCount }} 段 / {{ it.verseCount }} 句</td>
                        <td>
                            <span class="a-badge" :class="statusClass(it.status)">{{ statusLabel(it.status) }}</span>
                        </td>
                        <td>
                            <input
                                class="a-input sort-input"
                                type="number"
                                :value="it.sort"
                                @change="e => changeSort(it, e.target.value)"
                            />
                        </td>
                        <td class="muted">{{ it.viewcount }}</td>
                        <td class="muted small">{{ it.updateTime || '—' }}</td>
                        <td class="col-ops">
                            <button class="a-btn mini" @click="edit(it)">编辑</button>
                            <button class="a-btn mini" @click="preview(it)">预览</button>
                            <button
                                v-if="it.status !== 1"
                                class="a-btn mini primary"
                                @click="setStatus(it, 1)"
                            >发布</button>
                            <button
                                v-else
                                class="a-btn mini"
                                @click="setStatus(it, 2)"
                            >下线</button>
                            <button class="a-btn mini danger" @click="remove(it)">删除</button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- 分页 -->
            <div class="a-pager" v-if="total > query.size">
                <span>共 {{ total }} 条 · 第 {{ query.page }}/{{ pages }} 页</span>
                <button class="a-btn mini" :disabled="query.page <= 1" @click="go(query.page - 1)">上一页</button>
                <button class="a-btn mini" :disabled="query.page >= pages" @click="go(query.page + 1)">下一页</button>
            </div>
        </div>

        <transition name="fade">
            <div v-if="toast" class="a-toast" :class="toast.type">{{ toast.text }}</div>
        </transition>
    </div>
</template>

<script>
import { adminApi } from './api';

const THEME_LABEL = { letter: '信纸', rose: '红玫瑰', plain: '简约' };
const STATUS_LABEL = { 0: '草稿', 1: '已发布', 2: '已下线' };
const STATUS_CLASS = { 0: 'draft', 1: 'pub', 2: 'off' };

export default {
    name: 'AdminPages',
    data() {
        return {
            loading: false,
            seeding: false,
            items: [],
            total: 0,
            pages: 1,
            query: { keyword: '', status: 'all', page: 1, size: 20 },
            toast: null,
            toastTimer: null
        };
    },
    created() {
        this.load();
    },
    methods: {
        themeLabel(t) {
            return THEME_LABEL[t] || t || '—';
        },
        statusLabel(s) {
            return STATUS_LABEL[s] || '未知';
        },
        statusClass(s) {
            return STATUS_CLASS[s] || 'off';
        },
        notify(text, type) {
            this.toast = { text: text, type: type || 'ok' };
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
                this.toast = null;
            }, 2400);
        },
        load() {
            this.loading = true;
            adminApi
                .pages({
                    keyword: this.query.keyword,
                    status: this.query.status,
                    page: this.query.page,
                    size: this.query.size
                })
                .then(data => {
                    this.items = data.items || [];
                    this.total = data.total || 0;
                    this.pages = data.pages || 1;
                    if (data.seeded) {
                        this.notify('已自动导入内置页面 ' + data.seeded + ' 篇');
                    }
                })
                .catch(err => this.notify(err.message, 'err'))
                .finally(() => {
                    this.loading = false;
                });
        },
        search() {
            this.query.page = 1;
            this.load();
        },
        reset() {
            this.query.keyword = '';
            this.query.status = 'all';
            this.query.page = 1;
            this.load();
        },
        go(p) {
            if (p < 1 || p > this.pages) return;
            this.query.page = p;
            this.load();
        },
        doSeed() {
            this.seeding = true;
            adminApi
                .seed()
                .then(data => {
                    const n = (data && data.added) || 0;
                    this.notify(n > 0 ? '已导入 ' + n + ' 篇内置页面' : '内置页面已存在，无需重复导入');
                    this.load();
                })
                .catch(err => this.notify(err.message, 'err'))
                .finally(() => {
                    this.seeding = false;
                });
        },
        edit(it) {
            this.$router.push({ path: '/admin/edit', query: { id: it.id } });
        },
        preview(it) {
            this.$router.push({ path: '/admin/preview', query: { id: it.id } });
        },
        setStatus(it, status) {
            adminApi
                .status(it.id, status)
                .then(() => {
                    this.notify(status === 1 ? '已发布「' + it.title + '」' : '已下线「' + it.title + '」');
                    this.load();
                })
                .catch(err => this.notify(err.message, 'err'));
        },
        changeSort(it, value) {
            const s = parseInt(value, 10) || 0;
            if (s === it.sort) return;
            adminApi
                .sort(it.id, s)
                .then(() => {
                    it.sort = s;
                    this.notify('排序已更新为 ' + s);
                })
                .catch(err => this.notify(err.message, 'err'));
        },
        remove(it) {
            if (!window.confirm('确定删除「' + it.title + '」吗？删除后前台立即不可见，且无法恢复。')) {
                return;
            }
            adminApi
                .remove(it.id)
                .then(() => {
                    this.notify('已删除');
                    this.load();
                })
                .catch(err => this.notify(err.message, 'err'));
        }
    }
};
</script>

<style lang="less" scoped>
.admin-pages {
    .grow {
        flex: 1;
    }
    .muted {
        color: #8a7a6b;
    }
    .small {
        font-size: 12px;
    }
    .sort-input {
        width: 58px;
        height: 28px;
        padding: 0 7px;
        text-align: center;
    }
    .theme-chip {
        display: inline-block;
        padding: 2px 9px;
        font-size: 11.5px;
        line-height: 18px;
        border-radius: 6px;
        color: #7a6553;
        background: #f6efe6;
        border: 1px solid #e7d9c8;
        &.rose {
            color: #a3122e;
            background: #fdeef1;
            border-color: #f4ccd5;
        }
        &.plain {
            color: #4a6a7a;
            background: #eef4f7;
            border-color: #d6e4ec;
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
